import { MkIcon } from "@/mk/MkIcon";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { useMkRoot } from "@/app/branding";

const MARGIN = 8;
const STEP_MINUTES = 30;

function pad(value: number): string {
    return String(value).padStart(2, "0");
}

function isoDay(date: Date): string {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parse(value: string): { day: string; time: string } {
    const [day = "", time = ""] = String(value || "").split(/[T ]/);
    return { day: /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : "", time: time.slice(0, 5) };
}

/** A calendar that closes when the choice is made: on the day for a date, on
 *  the time for a date and time, so nobody has to close it by hand. */
export function MkDatePicker({ anchor, value, withTime, onPick, onClose }: {
    anchor: HTMLElement | null;
    value: string;
    withTime: boolean;
    onPick(iso: string): void;
    onClose(): void;
}) {
    const { t, i18n } = useTranslation();
    const root = useMkRoot();
    const panel = useRef<HTMLDivElement>(null);
    const times = useRef<HTMLDivElement>(null);
    const chosen = parse(value);
    const today = isoDay(new Date());
    const [day, setDay] = useState(chosen.day);
    const [month, setMonth] = useState(() => {
        const base = chosen.day ? new Date(`${chosen.day}T00:00:00`) : new Date();
        return new Date(base.getFullYear(), base.getMonth(), 1);
    });
    const locale = i18n.language || undefined;

    const weekdays = useMemo(() => {
        const sunday = new Date(2024, 0, 7);
        return Array.from({ length: 7 }, (_unused, index) => new Date(
            sunday.getFullYear(), sunday.getMonth(), sunday.getDate() + index,
        ).toLocaleDateString(locale, { weekday: "short" }));
    }, [locale]);

    const cells = useMemo(() => {
        const first = new Date(month.getFullYear(), month.getMonth(), 1);
        const start = new Date(first);
        start.setDate(first.getDate() - first.getDay());
        return Array.from({ length: 42 }, (_unused, index) => {
            const date = new Date(start);
            date.setDate(start.getDate() + index);
            return { iso: isoDay(date), date: date.getDate(), inMonth: date.getMonth() === month.getMonth() };
        });
    }, [month]);

    const slots = useMemo(() => Array.from({ length: (24 * 60) / STEP_MINUTES }, (_unused, index) => {
        const minutes = index * STEP_MINUTES;
        return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
    }), []);

    // keep the panel beside its field, above it when there is no room below
    useLayoutEffect(() => {
        const node = panel.current;
        if (!node || !anchor) {
            return undefined;
        }
        const place = () => {
            const rect = anchor.getBoundingClientRect();
            const viewport = window.visualViewport;
            const viewportTop = viewport?.offsetTop ?? 0;
            const viewportHeight = viewport?.height ?? window.innerHeight;
            node.style.maxHeight = `${Math.max(0, viewportHeight - MARGIN * 2)}px`;
            const width = node.offsetWidth;
            const height = node.offsetHeight;
            const below = viewportTop + viewportHeight - rect.bottom;
            const top = below < height + MARGIN && rect.top - viewportTop > height + MARGIN
                ? rect.top - height - 6 : rect.bottom + 6;
            const left = Math.min(Math.max(MARGIN, document.dir === "rtl" ? rect.right - width : rect.left),
                                  window.innerWidth - width - MARGIN);
            node.style.top = `${Math.max(viewportTop + MARGIN,
                Math.min(top, viewportTop + viewportHeight - height - MARGIN))}px`;
            node.style.left = `${left}px`;
        };
        place();
        window.addEventListener("scroll", place, true);
        window.addEventListener("resize", place);
        window.visualViewport?.addEventListener("resize", place);
        window.visualViewport?.addEventListener("scroll", place);
        return () => {
            window.removeEventListener("scroll", place, true);
            window.removeEventListener("resize", place);
            window.visualViewport?.removeEventListener("resize", place);
            window.visualViewport?.removeEventListener("scroll", place);
        };
    }, [anchor, withTime]);

    // the chosen time, or the working morning, starts in view
    useEffect(() => {
        const list = times.current;
        const target = list?.querySelector<HTMLElement>(".is-selected") ?? list?.querySelector<HTMLElement>("[data-time='08:00']");
        if (list && target) {
            list.scrollTop = target.offsetTop - list.clientHeight / 2 + target.offsetHeight / 2;
        }
    }, []);

    useEffect(() => {
        const away = (event: MouseEvent) => {
            const target = event.target as Node;
            if (!panel.current?.contains(target) && !anchor?.contains(target)) {
                onClose();
            }
        };
        const key = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.stopPropagation();
                onClose();
            }
        };
        document.addEventListener("mousedown", away, true);
        document.addEventListener("keydown", key, true);
        return () => {
            document.removeEventListener("mousedown", away, true);
            document.removeEventListener("keydown", key, true);
        };
    }, [anchor, onClose]);

    const pickDay = (iso: string) => {
        if (!withTime) {
            onPick(iso);
            onClose();
            return;
        }
        setDay(iso);
        // a time already set stays; the day alone is enough to save
        onPick(`${iso}T${chosen.time || "09:00"}`);
    };

    const pickTime = (time: string) => {
        onPick(`${day || today}T${time}`);
        onClose();
    };

    const shift = (months: number) => setMonth(new Date(month.getFullYear(), month.getMonth() + months, 1));

    return createPortal(
        <div className={`mk mk-datepicker${withTime ? " has-time" : ""}`} ref={panel} {...root}
             onMouseDown={(event) => event.preventDefault()}>
            <div className="mk-dp-calendar">
                <header className="mk-dp-head">
                    <button type="button" className="mk-dp-nav" onClick={() => shift(-1)} aria-label={t("mk.date.previous")}>
                        <MkIcon name="fa-chevron-left" className="mk-dp-flip" />
                    </button>
                    <strong>{month.toLocaleDateString(locale, { month: "long", year: "numeric" })}</strong>
                    <button type="button" className="mk-dp-nav" onClick={() => shift(1)} aria-label={t("mk.date.next")}>
                        <MkIcon name="fa-chevron-right" className="mk-dp-flip" />
                    </button>
                </header>
                <div className="mk-dp-grid">
                    {weekdays.map((name) => <span key={name} className="mk-dp-weekday">{name}</span>)}
                    {cells.map((cell) => (
                        <button key={cell.iso} type="button"
                                className={["mk-dp-day", cell.inMonth ? "" : "is-out", cell.iso === today ? "is-today" : "",
                                            cell.iso === day ? "is-selected" : ""].join(" ")}
                                onClick={() => pickDay(cell.iso)}>
                            {cell.date}
                        </button>
                    ))}
                </div>
                <footer className="mk-dp-foot">
                    <button type="button" onClick={() => {
                        setMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
                        pickDay(today);
                    }}>{t("mk.date.today")}</button>
                    {value && (
                        <button type="button" onClick={() => {
                            onPick("");
                            onClose();
                        }}>{t("mk.date.clear")}</button>
                    )}
                </footer>
            </div>
            {withTime && (
                <div className="mk-dp-times">
                    <span className="mk-dp-times-head"><MkIcon name="fa-clock-o" /> {t("mk.date.time")}</span>
                    <div className="mk-dp-time-list" ref={times}>
                        {slots.map((slot) => (
                            <button key={slot} type="button" data-time={slot}
                                    className={`mk-dp-time${slot === chosen.time ? " is-selected" : ""}`}
                                    onClick={() => pickTime(slot)}>
                                {slot}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>,
        document.body,
    );
}
